import { floatify } from 'lib/maths/mathUtils'
import { sum } from 'lib/array/arrayUtils'
import calculateAdjustedEarnTotals from './calculateAdjustedEarnTotals'

export interface BurnAdjustedEarnTotals {
  points: number
  basePoints: number
  bonusPoints: number
  statusCredits: number
  bonusStatusCredits: number
  remainingCash: number
}

export default function recalculatePointsEarnAfterBurn(
  pointsCalculations: Array<App.LoyaltyPointsEarnCalculation>,
  pointsToApply: number,
  priceInPoints: number,
): BurnAdjustedEarnTotals {
  const totalRequestPrice = sum(pointsCalculations, calc => calc.request.price ?? 0)

  if (pointsToApply <= 0 || priceInPoints <= 0 || totalRequestPrice <= 0) {
    return pointsCalculations.reduce<BurnAdjustedEarnTotals>((totals, calc) => ({
      points: totals.points + calc.points,
      basePoints: totals.basePoints + calc.basePoints,
      bonusPoints: totals.bonusPoints + calc.bonusPoints,
      statusCredits: totals.statusCredits + calc.statusCredits,
      bonusStatusCredits: totals.bonusStatusCredits + calc.bonusStatusCredits,
      remainingCash: totalRequestPrice,
    }), { points: 0, basePoints: 0, bonusPoints: 0, statusCredits: 0, bonusStatusCredits: 0, remainingCash: totalRequestPrice })
  }

  const totalPointsApplied = Math.min(pointsToApply, priceInPoints)
  const pointsCashValue = floatify((totalPointsApplied * totalRequestPrice) / priceInPoints)
  const remainingCash = Math.max(0, totalRequestPrice - pointsCashValue)
  const remainingRatio = remainingCash / totalRequestPrice

  return pointsCalculations.reduce<BurnAdjustedEarnTotals>((totals, pointsCalculation) => {
    const defaultPayableItemPrice = pointsCalculation.request.price ?? 0
    const adjustedTotals = calculateAdjustedEarnTotals({
      defaultPayableItemPrice,
      adjustedItemPrice: defaultPayableItemPrice * remainingRatio,
      basePoints: pointsCalculation.basePoints,
      bonusPoints: pointsCalculation.bonusPoints,
      statusCredits: pointsCalculation.statusCredits,
      bonusStatusCredits: pointsCalculation.bonusStatusCredits,
      roundingEnabled: pointsCalculation.roundingEnabled,
    })

    // `calculateAdjustedEarnTotals` re-derives status credits from the reduced base points using
    // the program-wide points→credit ratio. For high points-earn items (e.g. tours) the backend's
    // full-price `statusCredits` is capped well below that ratio, so the re-derived figure can come
    // out *higher* than the un-burned value — making displayed credits jump up when points are
    // applied. Burning points can only ever reduce earn, so clamp to the full-price base. (Checkout
    // avoids this by passing `earnCap` into `calculateAdjustedEarnTotals`, which this path lacks.)
    const earnableStatusCredits = Math.min(adjustedTotals.earnableStatusCredits, pointsCalculation.statusCredits)
    const earnableBonusStatusCredits = Math.min(adjustedTotals.earnableBonusStatusCredits, pointsCalculation.bonusStatusCredits)

    return {
      points: totals.points + adjustedTotals.earnablePoints,
      basePoints: totals.basePoints + adjustedTotals.earnableBasePoints,
      bonusPoints: totals.bonusPoints + adjustedTotals.earnableBonusPoints,
      statusCredits: totals.statusCredits + earnableStatusCredits,
      bonusStatusCredits: totals.bonusStatusCredits + earnableBonusStatusCredits,
      remainingCash: totals.remainingCash,
    }
  }, {
    points: 0,
    basePoints: 0,
    bonusPoints: 0,
    statusCredits: 0,
    bonusStatusCredits: 0,
    remainingCash,
  })
}
